import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, input, model, output, signal, untracked, viewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, finalize, map, race, Subject, switchMap, takeUntil, tap, throwError, timer } from 'rxjs';
import Typed from 'typed.js';
import { AIAgentService, AIAgentSession, AIAgentSessionMessage, AIAgentSessionMessageType } from '../../services/api.services';
import { AppError, catchAppError, escapeHtml } from '../../shared/utils';

const AI_AGENT_TIMEOUT = 1000 * 120;

@Component({
  selector: 'app-ai-agent-chat',
  templateUrl: './AIAgentChatComponent.html',
  styleUrl: './AIAgentChatComponent.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AIAgentChatComponent {
  readonly destroyRef   = inject(DestroyRef);
  readonly agentService = inject(AIAgentService);
  readonly MessageType  = AIAgentSessionMessageType;

  readonly sessionType    = input.required<string>();
  readonly sessionData    = model<any>(null);
  readonly autoSession    = input(true);

  readonly sessionId      = model(0);
  readonly session        = model<AIAgentSession>(null);
  readonly userPromptText = model('');
  readonly completed      = model(false);
  readonly failed         = model(false);

  readonly messages          = model<ReadonlyArray<AIAgentChatMessage>>([]);
  readonly messageIndex      = model(0);
  readonly typedMessageIndex = model(0);
  
  readonly creatingSession   = signal(false);
  readonly sendingUserPrompt = signal(false);
  readonly awaitingResponse  = signal(false);

  readonly busy = computed(() =>
    this.sendingUserPrompt() ||
    this.awaitingResponse());

  readonly canSendUserPrompt = computed(() =>
    !this.sendingUserPrompt() &&
    !this.awaitingResponse() &&
    !this.completed());


  readonly onSessionStarted    = input<AIAgentChatCallback<AIAgentSession>>(null);
  readonly onSessionCompleted  = input<AIAgentChatCallback<AIAgentSession>>(null);
  readonly onUserPrompt        = input<AIAgentChatCallback<AIAgentChatMessage>>(null);
  readonly onLastAgentResponse = input<AIAgentChatCallback<AIAgentChatMessage>>(null);

  private sessionCompletedSubject = new Subject<void>();

  private createSessionCompletedSubject() {
    this.sessionCompletedSubject.next();
    return this.sessionCompletedSubject = new Subject<void>();
  }

  readonly viewInitDone = signal(false);
  readonly currentSessionData = signal(null);

  readonly resetSessionOnDataChange = effect(() => {
    let [viewInitDone, sessionData, autoSession] = [this.viewInitDone(), this.sessionData(), this.autoSession()];
    if (viewInitDone && sessionData) {
      if (autoSession) {
        untracked(() => this.restartSession());
      }
    }
  });

  readonly newMessagePending = signal(false);

  ngAfterViewInit() {
    this.viewInitDone.set(true);
  }

  restartSession() {
    let completionSubject = this.createSessionCompletedSubject();

    this.messages.set([]);
    this.messageIndex.set(0);
    this.typedMessageIndex.set(0);
    this.failed.set(false);
    this.completed.set(false);

    this.sendingUserPrompt.set(false);
    this.awaitingResponse.set(false);

    this.creatingSession.set(true);

    let prevSessionData = this.currentSessionData();
    let sessionData = this.sessionData();
    let serializedData = JSON.stringify(sessionData);

    this.agentService.createSession({ type: this.sessionType(), serializedData }).pipe(
      takeUntilDestroyed(this.destroyRef),
      takeUntil(completionSubject),

      tap(s => {
        this.sessionId.set(s.id);
        this.session.set(s);

        this.currentSessionData.set(sessionData);

        completionSubject.subscribe(
          () => this.onSessionCompleted()?.call(this, { chat: this, data: s, sessionData: prevSessionData }));

        this.onSessionStarted()?.call(this, { chat: this, data: s, sessionData });
      }),
      catchAppError(),

      finalize(() => this.creatingSession.set(false))
    ).subscribe();
  }

  addMessage(message: AIAgentSessionMessage): AIAgentChatMessage;
  addMessage(message: AIAgentChatMessageInit): AIAgentChatMessage;
  addMessage(message: AIAgentSessionMessage | AIAgentChatMessageInit): AIAgentChatMessage {
    if (!('displayText' in message)) {
      let type = 
          message.type == AIAgentSessionMessageType.UserPrompt         ? AIAgentChatMessageType.UserPrompt 
        : message.type == AIAgentSessionMessageType.AgentResponse      ? AIAgentChatMessageType.AgentResponse 
        : message.type == AIAgentSessionMessageType.ApplicationToUser  ? AIAgentChatMessageType.System 
        : message.type == AIAgentSessionMessageType.AgentResponseError ? AIAgentChatMessageType.Error 
        : message.type == AIAgentSessionMessageType.UnexpectedError    ? AIAgentChatMessageType.Error
        : AIAgentChatMessageType.Debug;

      message = <AIAgentChatMessageInit>{
        type,
        timestamp:   message.timestamp,
        displayText: message.userMessage,
        value:       message,
        typed:       type != AIAgentChatMessageType.UserPrompt,
        visible:     type != AIAgentChatMessageType.Debug,
      };
    }

    if (!('timestamp' in message)) {
      message = {
        ...(message as any),
        timestamp: new Date().toISOString()
      };
    }
      
    if (!('typed' in message)) {
      message = {
        ...(message as any),
        typed: message.type != AIAgentChatMessageType.UserPrompt
      };
    }

    if (!('visible' in message)) {
      message = {
        ...(message as any),
        visible: message.type != AIAgentChatMessageType.Debug
      };
    }

    let index = this.messages().length;
    let chatMessage = new AIAgentChatMessage(index, message as any);

    this.messages.update(x => [...x, chatMessage]);
    this.messageIndex.update(x => Math.max(x, index));

    if (chatMessage.visible)
      this.newMessagePending.set(true);

    return chatMessage;
  }

  userPromptSend(textArea: HTMLTextAreaElement) {
    if (!this.canSendUserPrompt())
      return;

    let message = textArea.value;
    textArea.value = '';
    this.userPromptText.set('');

    this.sendingUserPrompt.set(true);

    this.addMessage({
      type: AIAgentSessionMessageType.UserPrompt,
      userMessage: message,
      timestamp: new Date().toISOString()
    });

    let sessionId = this.sessionId();

    let completionSubject = this.sessionCompletedSubject;

    this.onUserPrompt()?.call(this, { chat: this, data: message, sessionData: this.currentSessionData() });

    timer(100).pipe(
      takeUntilDestroyed(this.destroyRef),
      takeUntil(completionSubject),
      tap(() => this.scrollToBottom())
    ).subscribe();

    this.agentService.addUserPromptToSession(sessionId, message).pipe(
      takeUntilDestroyed(this.destroyRef),
      takeUntil(completionSubject),

      switchMap(executeModel => {
        this.awaitingResponse.set(true);

        const TIMEOUT_MARKER = {};
        let timeout = timer(AI_AGENT_TIMEOUT).pipe(map(() => TIMEOUT_MARKER));

        return race(this.agentService.next(sessionId, executeModel), timeout).pipe(
          takeUntilDestroyed(this.destroyRef),
          takeUntil(completionSubject),

          switchMap(x => {
            if (x === TIMEOUT_MARKER) {
              return throwError(() => new AppError({
                status: 500,
                message: 'timeout'
              }));
            }
            else {
              let lastCompleted: AIAgentChatMessage;

              for (let m of <AIAgentSessionMessage[]>x) {
                let message = this.addMessage(m);
                lastCompleted = message;
              }

              this.onLastAgentResponse()?.call(this, { chat: this, data: lastCompleted, sessionData: this.currentSessionData() });

              return EMPTY;
            }
          }),

          catchAppError((err: AppError) => {
            this.addMessage({
              type: AIAgentSessionMessageType.UnexpectedError,
              userMessage: err.message,
              timestamp: new Date().toISOString(),
            });

            return EMPTY;
          }),

          finalize(() => this.awaitingResponse.set(false))
        );
      }),

      catchAppError((err: AppError) => this.addMessage({
        type: AIAgentSessionMessageType.UnexpectedError,
        userMessage: err.message,
        timestamp: new Date().toISOString(),
      }), { showErrorSnack: false }),

      finalize(() => this.sendingUserPrompt.set(false))
    ).subscribe();
  }

  onMessageTyped(index: number) {
    this.typedMessageIndex.update(x => Math.max(x, index + 1));
  }

  readonly minTextAreaHeight   = signal(48);
  readonly showUserInputScroll = signal(false);

  onUserPromptTextInput(textArea: HTMLTextAreaElement) {
    const LINE_HEIGHT = 24;
    let lineCount = textArea.value.split('\n').length;

    let minHeight = LINE_HEIGHT * 2;
    let maxHeight = minHeight + LINE_HEIGHT * 1;
    let height    = lineCount * LINE_HEIGHT;

    this.minTextAreaHeight.set(Math.min(maxHeight, Math.max(minHeight, height)));
  }

  readonly chatTopElement = viewChild<ElementRef<HTMLElement>>('chatTop');

  readonly autoScrolling  = signal(false);
  readonly userScrolled   = signal(false);
  readonly isScrolledDown = signal(false);

  readonly chatTopElementEffect = effect(() => {
    let chatTop = this.chatTopElement()?.nativeElement;
    if (chatTop) {
      chatTop.addEventListener('scroll', e => {
        if (this.autoScrolling())
          return;

        this.userScrolled.set(true);
        this.isScrolledDown.set(false);
      });
    }
  });

  readonly autoScrollTimer = timer(0, 200).pipe(
    takeUntilDestroyed(this.destroyRef),
    tap(() => {
      if (this.userScrolled())
        return;

      this.scrollToBottom();
    })
  ).subscribe();

  scrollToBottom() {
    this.userScrolled.set(false);

    let chatTop = this.chatTopElement()?.nativeElement;
    if (chatTop) {
      try {
        this.autoScrolling.set(true);

        chatTop.scrollTo({
          left: 0,
          top: chatTop.scrollHeight,
          behavior: 'smooth',
        });

        this.isScrolledDown.set(true);
        this.newMessagePending.set(false);
      }
      finally {
        timer(100).pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(() => this.autoScrolling.set(false))
        ).subscribe();
      }
    }
  }
}

export interface AIAgentChatCallbackArgs<TData = any, TSessionData = any> {
  chat: AIAgentChatComponent;
  data: TData;
  sessionData: TSessionData;
};

export type AIAgentChatCallback<TData = any, TSessionData = any> = (args: AIAgentChatCallbackArgs<TData, TSessionData>) => void;

export enum AIAgentChatMessageType {
  UserPrompt = 'userPrompt',
  AgentResponse = 'agentResponse', 
  System = 'system',
  Error = 'error',
  Debug = 'debug',
}

let nextMessageId = 1;

export interface AIAgentChatMessageInit {
  readonly type: AIAgentChatMessageType;
  readonly timestamp?: string;
  readonly displayText: string;
  readonly value?: AIAgentSessionMessage;
  readonly typed?: boolean;
  readonly visible?: boolean;
}

export class AIAgentChatMessage {
  readonly id = nextMessageId++;

  constructor(
    readonly index: number,
    private readonly init: AIAgentChatMessageInit) { }

  readonly type        = this.init.type;
  readonly timestamp   = this.init.timestamp;
  readonly displayText = this.init.displayText;
  readonly value       = this.init.value;
  readonly typed       = this.init.typed;
  readonly visible     = this.init.visible;

  readonly hasResult = computed(() => !!this.value?.hasResult);
  readonly hasError  = computed(() => this.type == AIAgentChatMessageType.Error);

  readonly hasResultOrError = computed(() => this.hasResult() || this.hasError());
}

@Component({
  selector: 'app-ai-agent-chat-message',
  template: `
    @let m = message();
    <div #messageDiv class="chat-message" [hidden]="!visible()">
      <div class="text" #textElement>
      </div>
    </div>
  `,
  styleUrl: './AIAgentChatComponent.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AIAgentChatMessageComponent {
  readonly destroyRef  = inject(DestroyRef);
  readonly MessageType = AIAgentChatMessageType;
  readonly chat        = inject(AIAgentChatComponent);

  readonly message     = input.required<AIAgentChatMessage>();
  readonly index       = computed(() => this.message()?.index);
  readonly type        = computed(() => this.message()?.type);
  readonly timestamp   = computed(() => this.message()?.timestamp);
  readonly displayText = computed(() => this.message()?.displayText);
  readonly typed       = computed(() => this.message()?.typed);

  readonly visible = computed(() => {
    if (!this.message()?.visible)
      return false;

    if (this.typed()) {
      return this.chat.typedMessageIndex() >= this.index();
    }
    else {
      return this.chat.messageIndex() >= this.index();
    }
  });


  readonly messageDiv  = viewChild<ElementRef<HTMLElement>>('messageDiv');
  readonly textElement = viewChild<ElementRef<HTMLElement>>('textElement');

  readonly messageTyped = output();

  readonly applyMessageDivClassNames = effect(() => {
    let messageDiv = this.messageDiv();
    if (messageDiv?.nativeElement) {
      let className = `chat-message__${this.type() ?? 'unknown'}`;

      let toRemove = Array
        .from(messageDiv.nativeElement.classList)
        .filter(x => x.startsWith('chat-message__'));

      toRemove.forEach(x =>
        messageDiv.nativeElement.classList.remove(x));

      messageDiv.nativeElement.classList.add(className);
    }
  })

  readonly applyDisplayText = effect(() => {
    let [visible, textElement] = [this.visible(), this.textElement()];

    if (visible && textElement) {
      let [displayText, typed] = [this.displayText(), this.typed()];
      if (typed) {
        let escapedDisplayText = escapeHtml(displayText);

        let typedInst = new Typed(textElement.nativeElement, {
          strings:    [escapedDisplayText],
          typeSpeed:  3,
          onComplete: _ => this.messageTyped.emit(),
          showCursor: false,
          contentType: 'html'
        });

        this.destroyRef.onDestroy(() => typedInst.destroy());
      }
      else {
        textElement.nativeElement.innerHTML = displayText;
        this.messageTyped.emit();
      }
    }
  });
}
